import { GlobalStoreContextType } from "../context/GlobalStoreContext";
import { DeliveryModel } from "./Delivery.model";

export class Delivery extends DeliveryModel {
    receivingTeamId(): number | null {
        return this.deliveryHasTeams?.find((team) => team.deliveryRole == "receiving_team")?.teamId || null;
    }

    gcTeamId(): number | null {
        return this.deliveryHasTeams?.find((team) => team.deliveryRole == "gc_team")?.teamId ?? null;
    }

    /**
     * Does this delivery have the GC team tagged for approval? This would mean another team is the receiving team and they are just attached for approval
     * @returns
     */
    hasGCTeamTaggedForApproval(): boolean {
        return this.gcTeamId() != null;
    }

    vendorTravelDistanceLocalized(): number | null {
        // TODO: Check is project settings use imperial or metric when we add localization
        return this.vendorTravelDistance ? Math.round(this.vendorTravelDistance / 1609.344) : null;
    }

    deliveryReceivingTeamLogic(context: GlobalStoreContextType): string {
        switch (this.deliveryHasTeams?.length) {
            case 1: {
                const team = this.deliveryHasTeams?.[0];
                const teamInfo = context.teamsForProject(this.projectId || "").find((t) => t.id === team?.teamId);
                return teamInfo ? teamInfo.team_name : "Unnamed team";
            }
            default: {
                const deliveryProject = context.currentTeamsProjects.member_of.find((p) => p.id === this.projectId);
                if (!this.deliveryHasTeams) {
                    return "No teams assigned";
                }
                return (
                    this.deliveryHasTeams
                        ?.filter((t) => t.teamId !== deliveryProject?.owning_team_id)
                        ?.map((team) => {
                            if (team.teamId == null) return "Unnamed team";
                            const deliveryTeam = context
                                .teamsForProject(this.projectId || "")
                                .find((t) => t.id === team.teamId);
                            return deliveryTeam ? deliveryTeam.team_name : "Unnamed team";
                        })
                        .join(", ") || "No teams assigned"
                );
            }
        }
    }

    toCsvObject(context: GlobalStoreContextType): any {
        return {
            id: this.id,
            archived: this.archived,
            arrival_time: this.arrivalTime,
            calendar_event_channel_active: this.calendarEventChannelActive,
            created_at: this.createdAt,
            created_by_user_id: this.createdByUserId,
            creation_location: this.creationLocation,
            delivery_contact_info: this.deliveryContactInfo,
            delivery_route: this.deliveryRoute,
            departure_time: this.departureTime,
            description: this.description,
            driver_estimated_travel_time: this.driverEstimatedTravelTime,
            drop_off_area_id: this.dropOffAreaId,
            drop_off_area: this.dropOffAreaId
                ? (context.mapAreasForProject(this.projectId).find((area) => area.id === this.dropOffAreaId)
                      ?.areaName ?? null)
                : null,
            drop_off_area_uuid: this.dropOffAreaUuid,
            end_date_time: this.endDateTime,
            eta: this.eta,
            eta_selected: this.etaSelected,
            final_approved: this.finalApproved,
            idle_time: this.idleTime,
            integration_log_id: this.integrationLogId,
            project_id: this.projectId,
            requested_override: this.requestedOverride,
            start_date_time: this.startDateTime,
            status: this.status,
            title: this.title,
            travel_distance: this.travelDistance,
            unique_token: this.uniqueToken,
            updated_at: this.updatedAt,
            vehicle_quantity: this.vehicleQuantity,
            vehicle_type: this.vehicleType,
            vendor_directions: this.vendorDirections,
            vendor_id: this.vendorId,
            vendor_travel_distance: this.vendorTravelDistance,
            vendor_travel_time: this.vendorTravelTime,
            vendor_user_id: this.vendorUserId,
            waypoint_area_id: this.waypointAreaId,
            waypoint_uuid: this.waypointUuid,
            audits: this.audits?.map((item) => item.toApiObject()),
            deliveries_equipment_attributes: this.deliveriesEquipment?.map((item) => item.toApiObject()),
            delivery_equipment:
                this.deliveriesEquipment
                    ?.map(
                        (item) =>
                            context.equipmentForProject(this.projectId).find((eq) => eq.id === item.equipmentId)
                                ?.equipmentName
                    )
                    .join(", ") || "No equipment",
            delivery_has_teams_attributes: this.deliveryHasTeams?.map((item) => item.toApiObject()),
            delivery_teams: this.deliveryReceivingTeamLogic(context),
            orders: this.orders?.map((item) => item.toApiObject()),
            _destroy: this._destroy,
        };
    }
}
